import * as React from 'react';
import Table, { ColumnType } from 'antd/lib/table';
import Pagination from 'antd/lib/pagination';
import Result from 'antd/lib/result';
import Button from 'antd/lib/button';
import Skeleton from 'antd/lib/skeleton';
import Empty from 'antd/lib/empty';
import Alert from 'antd/lib/alert';
import Text from 'antd/lib/typography/Text';
import { MehOutlined } from '@ant-design/icons';
import { SummaryViewContextValue } from '@/utils/hooks/useSummaryView';
import { SummaryViewTableRow, TabularLayoutType } from '@/types/summaryView';
import { makeDraggable } from '@/helpers/summaryViewHelpers';
import { antGrey } from '@/components/styles/globalStyles';
import type { DragEndEvent, DragOverEvent } from '@dnd-kit/core';
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
    arrayMove,
    horizontalListSortingStrategy,
    verticalListSortingStrategy,
    SortableContext,
} from '@dnd-kit/sortable';
import {
    DragIndexContext,
    DragIndexState,
    TableHeaderCell,
    TableBodyCell,
    TableBodyRow,
} from '@/components/orgSummary/DraggableTableComponents';

interface SummaryTableProps {
    summary: SummaryViewContextValue;
}

type SummaryColumn = ColumnType<SummaryViewTableRow>;

// antd small cell: 8px padding top and bottom plus the 1px row border; heights snap to whole 22px text lines
const ROW_CHROME_HEIGHT = 17;
const LINE_HEIGHT = 22;
const linesForRowHeight = (height: number) => Math.max(1, Math.round((height - ROW_CHROME_HEIGHT) / LINE_HEIGHT));
const rowHeightForLines = (lines: number) => ROW_CHROME_HEIGHT + lines * LINE_HEIGHT;

const MIN_TABLE_HEIGHT = 240;
const scrollParent = (el: HTMLElement) => {
    let node = el.parentElement;
    while (node && !/auto|scroll/.test(getComputedStyle(node).overflowY)) node = node.parentElement;
    return node;
};

export const SummaryTable: React.FC<SummaryTableProps> = ({ summary }) => {
    const {
        preferences,
        error: { shouldDisplayFetchFailed, setShouldDisplayFetchFailed },
        refetchCurrentPage,
        loading: { isLoading },
        tableData,
        pagination: { currentPage, pageSize, setCurrentPage },
        summaryViewResponse,
        updatePreferences,
        setStandardRowOrder,
        selectedTotalCount,
        availableTotalCount,
        isLoadingCount,
    } = summary;

    const { tabularLayout, expandRows } = preferences;
    const isExpanded = !!expandRows?.[tabularLayout];
    const { standardColumns, compactConfig, filteredRows } = tableData;

    const isCompactLayout = tabularLayout === TabularLayoutType.Compact;
    const tableDataSource = isCompactLayout ? compactConfig.rows : filteredRows;
    const hasActualData = filteredRows.length > 0;
    const hasFetchCompleted = summaryViewResponse !== undefined || shouldDisplayFetchFailed;
    const showSkeletonInEmptyState = !hasFetchCompleted || (!hasActualData && isLoading);
    const [dragIndex, setDragIndex] = React.useState<DragIndexState>({ active: -1, over: -1 });

    const draggableStandardColumns = React.useMemo(() => makeDraggable(standardColumns), [standardColumns]);
    const draggableCompactColumns = React.useMemo(() => makeDraggable(compactConfig.columns), [compactConfig.columns]);

    const skeletonDataSource = React.useMemo(() => {
        if (!showSkeletonInEmptyState) return tableDataSource;

        return Array.from({ length: 1 }).map((_, index) => ({
            key: `skeleton-${index}`,
            isSkeleton: true,
        }));
    }, [showSkeletonInEmptyState, tableDataSource]);

    const columnsWithSkeleton = React.useMemo(() => {
        if (!showSkeletonInEmptyState) return standardColumns;

        return standardColumns.map((col) => ({
            ...col,
            render: (value: any, record: any, index: number) => {
                if (record.isSkeleton) {
                    return <Skeleton.Input active size="small" style={{ width: '100%' }} />;
                }

                return col.render ? col.render(value, record, index) : value;
            },
        }));
    }, [showSkeletonInEmptyState, standardColumns]);

    const showHeader = !(showSkeletonInEmptyState && isCompactLayout);

    const sortableColumnKeys = React.useMemo(() => {
        if (isCompactLayout) {
            return compactConfig.columns
                .filter((col) => !('fixed' in col && col.fixed))
                .map((col) => col.key as string);
        }

        return draggableStandardColumns.filter((col) => !col.fixed).map((col) => col.key as string);
    }, [isCompactLayout, draggableStandardColumns, compactConfig.columns]);

    const sortableRowKeys = React.useMemo(() => {
        const source = isCompactLayout ? compactConfig.rows : filteredRows;
        return source.map((row) => row.key);
    }, [isCompactLayout, filteredRows, compactConfig.rows]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
    );

    const onDragEnd = React.useCallback(
        ({ active, over }: DragEndEvent) => {
            if (active.id !== over?.id) {
                const dragType = active.data.current?.type as 'column' | 'row';
                const keys = dragType === 'row' ? sortableRowKeys : sortableColumnKeys;
                const activeIndex = keys.findIndex((key) => key === active.id);
                const overIndex = keys.findIndex((key) => key === over?.id);
                if (activeIndex === -1 || overIndex === -1) return;

                const newOrder = arrayMove(keys, activeIndex, overIndex);

                if (isCompactLayout) {
                    const key = dragType === 'row' ? 'metricRowOrder' : 'destinationColumnOrder';
                    updatePreferences({ dragOrderPreferences: { compact: { [key]: newOrder } } });
                } else if (dragType === 'row') {
                    setStandardRowOrder(newOrder);
                } else {
                    updatePreferences({ dragOrderPreferences: { standard: { metricColumnOrder: newOrder } } });
                }
            }

            setDragIndex({ active: -1, over: -1 });
        },
        [sortableColumnKeys, sortableRowKeys, isCompactLayout, setStandardRowOrder, updatePreferences],
    );

    const onDragOver = React.useCallback(
        ({ active, over }: DragOverEvent) => {
            const dragType = (active.data.current?.type as 'column' | 'row') ?? 'column';
            const keys = dragType === 'row' ? sortableRowKeys : sortableColumnKeys;
            const activeIndex = keys.findIndex((key) => key === active.id);
            const overIndex = keys.findIndex((key) => key === over?.id);

            setDragIndex({
                active: active.id,
                over: over?.id,
                direction: overIndex > activeIndex ? 'right' : 'left',
                dragType,
            });
        },
        [sortableColumnKeys, sortableRowKeys],
    );

    const finalColumns = React.useMemo(() => {
        if (showSkeletonInEmptyState) return columnsWithSkeleton;
        return isCompactLayout ? draggableCompactColumns : draggableStandardColumns;
    }, [
        showSkeletonInEmptyState,
        columnsWithSkeleton,
        isCompactLayout,
        draggableCompactColumns,
        draggableStandardColumns,
    ]);

    const scrollRef = React.useRef<HTMLDivElement>(null);
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const [maxHeight, setMaxHeight] = React.useState<number>();
    const [rowHeights, setRowHeights] = React.useState<Record<string, number>>({});

    // fills the pane down to the footer so the table scrolls, not the page
    React.useLayoutEffect(() => {
        const wrapper = wrapperRef.current;
        const pane = wrapper && scrollParent(wrapper);
        if (!wrapper || !pane) return undefined;
        const fit = () => {
            const top = wrapper.getBoundingClientRect().top - pane.getBoundingClientRect().top + pane.scrollTop;
            const paddingBottom = parseFloat(getComputedStyle(pane).paddingBottom) || 0;
            setMaxHeight(Math.max(MIN_TABLE_HEIGHT, pane.clientHeight - paddingBottom - top));
        };
        fit();
        const observer = new ResizeObserver(fit);
        observer.observe(pane);
        if (pane.firstElementChild) observer.observe(pane.firstElementChild);
        return () => observer.disconnect();
    }, []);

    const commitRowHeight = React.useCallback((key: string, height?: number, allRows?: boolean) => {
        const snapped = height === undefined ? undefined : rowHeightForLines(linesForRowHeight(height));
        const keys =
            allRows ?
                Array.from(scrollRef.current?.querySelectorAll<HTMLElement>('tr[data-row-key]') ?? [], (tr) =>
                    String(tr.dataset.rowKey),
                )
            :   [key];
        React.startTransition(() =>
            setRowHeights((prev) => {
                const next = { ...prev };
                keys.forEach((k) => {
                    if (snapped === undefined) delete next[k];
                    else next[k] = snapped;
                });
                return next;
            }),
        );
    }, []);

    // the toggle sets every row's height, so it discards manual row sizes
    React.useEffect(() => setRowHeights({}), [isExpanded]);

    const tableColumns = React.useMemo(
        () =>
            finalColumns.map((col: SummaryColumn, columnIndex: number) => ({
                ...col,
                onCell: (record: SummaryViewTableRow, index?: number) => ({
                    ...col.onCell?.(record, index),
                    ...(columnIndex === 0 && {
                        onRowResize: (height?: number, allRows?: boolean) =>
                            commitRowHeight(record.key, height, allRows),
                    }),
                }),
            })),
        [finalColumns, commitRowHeight],
    );

    const paginationSummaryText = React.useMemo(() => {
        if (selectedTotalCount === null || availableTotalCount === null || isLoadingCount) {
            return null;
        }

        const start = filteredRows.length > 0 ? (currentPage - 1) * pageSize + 1 : 0;
        const end = filteredRows.length > 0 ? start + filteredRows.length - 1 : 0;

        return `Showing ${start}\u2013${end} of ${selectedTotalCount.toLocaleString()} selected (${availableTotalCount.toLocaleString()} available)`;
    }, [selectedTotalCount, availableTotalCount, isLoadingCount, filteredRows.length, currentPage, pageSize]);
    const shouldShowFooter = !!paginationSummaryText || (selectedTotalCount !== null && selectedTotalCount > pageSize);

    const renderTable = () => (
        <Table
            size="small"
            pagination={false}
            showHeader={showHeader}
            rowKey={(row) => row.key}
            scroll={{ x: 'max-content' }}
            className={isExpanded ? 'summary-table-expanded' : undefined}
            onRow={(row) => {
                const height = rowHeights[row.key];
                if (height === undefined) return {};
                return {
                    className: 'summary-row-sized',
                    style: { height, '--summary-row-lines': linesForRowHeight(height) } as React.CSSProperties,
                };
            }}
            style={{ fontSize: 6, flexShrink: 0 }}
            columns={tableColumns}
            dataSource={skeletonDataSource}
            locale={{
                emptyText: <Empty description="No data" />,
            }}
            components={{
                header: { cell: TableHeaderCell },
                body: { row: TableBodyRow, cell: TableBodyCell },
            }}
        />
    );

    return (
        <div
            ref={wrapperRef}
            style={{
                width: '100%',
                maxHeight,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            }}
        >
            {shouldDisplayFetchFailed ?
                <Result
                    icon={<MehOutlined style={{ color: antGrey.grey5 }} />}
                    subTitle="Error fetching summary view"
                    extra={
                        <Button
                            onClick={() => {
                                setShouldDisplayFetchFailed(false);
                                refetchCurrentPage();
                            }}
                        >
                            Try again
                        </Button>
                    }
                />
            :   <>
                    {summaryViewResponse?.searchIncomplete && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 8 }}
                            message="Still indexing some releases — a few matches may be missing. Retry shortly."
                        />
                    )}
                    <div
                        ref={scrollRef}
                        className="summary-table-scroll"
                        style={{
                            display: isCompactLayout ? 'flex' : 'block',
                            flex: 1,
                            minHeight: 0,
                            overflow: 'auto',
                        }}
                    >
                        <DndContext
                            sensors={sensors}
                            onDragEnd={onDragEnd}
                            onDragOver={onDragOver}
                            collisionDetection={closestCenter}
                        >
                            <SortableContext items={sortableColumnKeys} strategy={horizontalListSortingStrategy}>
                                <SortableContext items={sortableRowKeys} strategy={verticalListSortingStrategy}>
                                    <DragIndexContext.Provider value={dragIndex}>
                                        {renderTable()}
                                    </DragIndexContext.Provider>
                                </SortableContext>
                            </SortableContext>
                        </DndContext>
                    </div>
                    {shouldShowFooter && (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '20px 0 32px',
                                gap: 16,
                            }}
                        >
                            <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                {paginationSummaryText ?? ''}
                            </Text>
                            {selectedTotalCount !== null && selectedTotalCount > pageSize && (
                                <Pagination
                                    size="small"
                                    current={currentPage}
                                    pageSize={pageSize}
                                    total={selectedTotalCount}
                                    onChange={setCurrentPage}
                                    hideOnSinglePage
                                    showSizeChanger={false}
                                    disabled={isLoading}
                                    responsive
                                />
                            )}
                        </div>
                    )}
                </>
            }
        </div>
    );
};
